import React, { useState } from 'react';
import { FileCheck, Clock, Shield, CheckCircle, AlertTriangle, Target, Award, ChevronDown, ChevronUp, FileText, Zap } from 'lucide-react';
import RegistrationModal from '../../components/RegistrationModal';
import SEO from '../../components/SEO';

const StatementOfUsePage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [modalMode, setModalMode] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const openModal = (mode) => {
    setModalMode(mode);
  };

  const closeModal = () => {
    setModalMode(null);
  };

  const benefits = [
    {
      icon: Shield,
      title: "Secure Registration",
      description: "Complete your intent-to-use application and convert it to full federal trademark registration."
    },
    {
      icon: Clock,
      title: "Extension Management",
      description: "Track deadlines and file extensions to maintain your application while developing commercial use."
    },
    {
      icon: FileCheck,
      title: "Specimen Preparation",
      description: "Expert guidance on acceptable specimens showing proper trademark use in commerce."
    },
    {
      icon: Target,
      title: "Priority Rights",
      description: "Maintain your priority filing date from your original application through registration."
    },
    {
      icon: Award,
      title: "Compliance Assurance",
      description: "Attorney-prepared filings meeting all USPTO requirements to avoid rejection."
    },
    {
      icon: Zap,
      title: "Fast Processing",
      description: "Expedited preparation and filing to meet critical USPTO deadlines."
    }
  ];

  const requirements = [
    {
      title: "Commercial Use",
      description: "Actual use of the trademark in commerce for all goods/services claimed",
      examples: ["Products sold to customers", "Services provided", "Interstate commerce", "Bona fide transactions"]
    },
    {
      title: "Acceptable Specimens",
      description: "Evidence showing trademark as used in commerce",
      examples: ["Product labels/packaging", "Website screenshots", "Marketing materials", "Service advertisements"]
    },
    {
      title: "Date of First Use",
      description: "Documentation of when trademark was first used commercially",
      examples: ["Invoice dates", "Sales records", "Launch documentation", "Dated photographs"]
    },
    {
      title: "Sworn Declaration",
      description: "Verified statement of use meeting USPTO requirements",
      examples: ["Proper wording", "Authorized signatory", "Good faith declaration", "Penalty of perjury"]
    }
  ];

  const process = [
    {
      step: "1",
      title: "Use Assessment",
      description: "Evaluate your commercial use, specimens, and readiness to file Statement of Use.",
      timeline: "1-2 days"
    },
    {
      step: "2",
      title: "Specimen Review",
      description: "Analyze proposed specimens for USPTO acceptability and compliance with requirements.",
      timeline: "2-3 days"
    },
    {
      step: "3",
      title: "Filing Preparation",
      description: "Prepare Statement of Use with specimens, dates, and sworn declaration.",
      timeline: "3-5 days"
    },
    {
      step: "4",
      title: "USPTO Submission",
      description: "File with USPTO and monitor for approval or office actions requiring response.",
      timeline: "2-4 months"
    }
  ];

  const packages = [
    {
      name: "Basic SOU",
      price: "$299",
      description: "Single class filing",
      features: [
        "Statement of Use preparation",
        "1 class of goods/services",
        "Specimen review",
        "USPTO filing",
        "Filing receipt",
        "Email support"
      ],
      popular: false
    },
    {
      name: "Professional SOU",
      price: "$499",
      description: "Multi-class with support",
      features: [
        "Everything in Basic",
        "Up to 3 classes",
        "Multiple specimen options",
        "Comprehensive review",
        "Office action response (1)",
        "Priority support",
        "Status tracking"
      ],
      popular: true
    },
    {
      name: "Extension Filing",
      price: "$199",
      description: "Per extension request",
      features: [
        "Extension of time filing",
        "All required documentation",
        "USPTO filing",
        "Deadline tracking",
        "Email confirmation",
        "Up to 5 extensions total"
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      question: "What is a Statement of Use and when do I need to file it?",
      answer: "A Statement of Use (SOU) is a sworn declaration filed with the USPTO confirming you've begun using your trademark in commerce for the goods/services specified in your intent-to-use application. It's required to complete registration of trademarks filed under intent-to-use (1(b)) basis. You must file within 6 months of receiving a Notice of Allowance from the USPTO, though extensions are available. Without a timely SOU, your application will be abandoned and you'll lose your priority filing date."
    },
    {
      question: "How long do I have to file a Statement of Use?",
      answer: "You have 6 months from the Notice of Allowance date to file your Statement of Use. If you need more time, you can request up to five 6-month extensions (total 36 months) by paying $125 per extension per class. First extension requires only 'continued efforts' to use the mark. Subsequent extensions require showing 'good cause' for delay. Extensions must be filed before the current deadline expires. Missing deadlines results in application abandonment—we track deadlines and file extensions to protect your priority date."
    },
    {
      question: "What specimens are acceptable for a Statement of Use?",
      answer: "Acceptable specimens must show the trademark as actually used in commerce. For goods: labels, tags, packaging, containers, displays, or product photographs showing the mark. For services: advertisements, brochures, website pages, or marketing materials promoting services under the mark. Specimens must show: (1) the mark as applied for, (2) association with goods/services, and (3) use in commerce (not just promotional). Common rejections: mock-ups, digitally inserted marks, ornamental use on clothing, or specimens not showing trademark use. We review specimens before filing to ensure acceptance."
    },
    {
      question: "What is the 'date of first use' and why does it matter?",
      answer: "Date of first use is when you first used the trademark in commerce for the goods/services claimed. Two dates required: (1) date of first use anywhere, and (2) date of first use in interstate commerce. These establish your common law rights and can affect priority disputes. Dates must be accurate and supportable—false statements can invalidate your registration. If you're unsure of exact dates, we help establish them through business records, invoices, dated photographs, or other documentation. The dates cannot be before your application filing date."
    },
    {
      question: "Can I file a Statement of Use before actually using my trademark?",
      answer: "No, you must have bona fide use in commerce before filing. The Statement of Use includes a sworn declaration under penalty of perjury that you're using the mark. 'Token use' doesn't qualify—you need actual commercial transactions with customers. Filing false statements of use can result in registration cancellation, fraud findings, and potential criminal penalties. If you're not ready to use the mark, file extension requests to maintain your application while developing actual use. We assess your use before filing to ensure compliance."
    },
    {
      question: "What if I'm only using the trademark for some of the goods/services in my application?",
      answer: "You can file Statement of Use for only the goods/services you're using and either: (1) divide the application, maintaining the unused classes for later filing, or (2) delete unused goods/services (cannot add them back later). Division allows you to register used classes immediately while maintaining intent-to-use basis for others. This is strategic when you've launched some products but not others. We help you decide whether to divide or delete based on your business plans and deadlines."
    },
    {
      question: "How much does filing a Statement of Use cost?",
      answer: "USPTO filing fee: $100 per class (TEAS Plus) or $150 per class (TEAS Standard). Our attorney preparation fees: $299 for basic single-class SOU, $499 for professional multi-class service. Extension requests: $125 per class per extension (USPTO fee) plus $199 preparation fee. Total investment ensures proper specimen selection, accurate dates, and compliant declarations—avoiding costly rejections or refiling. Rejected SOUs require new filing fees, making professional preparation cost-effective."
    },
    {
      question: "What happens after I file my Statement of Use?",
      answer: "USPTO examines your Statement of Use (typically 2-4 months). Possible outcomes: (1) Approval: mark proceeds to registration and certificate issues within 2-3 months, (2) Office Action: examiner requests clarifications or additional specimens (we respond), (3) Rejection: specimens unacceptable or use insufficient (can refile with proper specimens). If approved, you receive registration certificate and your mark is fully registered with all federal protections. We monitor your application and handle any office actions to ensure successful registration."
    },
    {
      question: "Can I file multiple Statements of Use for different classes?",
      answer: "Yes, if you have a multi-class application, you can file separate Statements of Use as you begin using the mark for different goods/services. File divisional requests to separate classes, then file individual SOUs with class-specific specimens and use dates. This is common when launching products in phases. Each division requires separate filing fees. Strategy depends on your launch timeline and business plans. We help coordinate divisional filings and staggered SOUs to optimize registration timing while managing costs."
    },
    {
      question: "What if my Statement of Use is rejected?",
      answer: "If rejected, you have 6 months to respond with: (1) different specimens showing acceptable use, (2) arguments why original specimens are acceptable, (3) amendments to goods/services descriptions, or (4) evidence of proper use. Common rejection reasons: insufficient specimens, ornamental use, specimens showing different mark, or no trademark use shown. If unable to overcome rejection, you can: refile new SOU when you have acceptable specimens (with new fee), request additional time to develop proper use, or appeal examiner's decision. We handle all rejection responses and strategy."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Statement of Use Filing Services - Complete Your Trademark Registration"
        description="Professional Statement of Use preparation and filing to complete your intent-to-use trademark application. Expert specimen review and deadline management."
        keywords="statement of use, SOU filing, trademark completion, intent to use, trademark specimens"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FF6B35]/20 text-[#FF6B35] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <FileCheck className="w-4 h-4" />
                <span>Statement of Use Services</span>
              </div>
              <h1 className="text-5xl font-bold mb-6 leading-tight">
                Complete Your Trademark Registration with Statement of Use
              </h1>
              <p className="text-xl mb-8 text-gray-300">
                Professional Statement of Use preparation and filing to convert your intent-to-use application into full federal registration. Expert specimen review and deadline management.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => openModal('registration')}
                  className="bg-[#FF6B35] hover:bg-[#FF8C42] text-white px-8 py-4 rounded-lg font-semibold transition-all hover:shadow-xl hover:scale-105"
                >
                  File Statement of Use
                </button>
                <button 
                  onClick={() => {
                    if (window.cd360Api && window.cd360Api.liveChat) {
                      window.cd360Api.liveChat.showWebWidget();
                    } else {
                      console.log('Chat widget not loaded yet');
                    }
                  }}
                  className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all"
                >
                  Speak with Attorney
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Specimen Review</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Deadline Tracking</span>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-6">Critical Deadlines</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">6-Month Deadline</strong>
                    <span className="text-gray-300">File Statement of Use within 6 months of Notice of Allowance</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Extensions Available</strong>
                    <span className="text-gray-300">Up to 5 extensions (36 months total) if you need more time</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Abandonment Risk</strong>
                    <span className="text-gray-300">Missing deadlines results in application abandonment</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Professional Statement of Use Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert preparation to ensure successful registration
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100">
                <benefit.icon className="w-12 h-12 text-[#FF6B35] mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Statement of Use Requirements
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              What you need to complete your trademark registration
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {requirements.map((req, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{req.title}</h3>
                <p className="text-gray-600 mb-6">{req.description}</p>
                <div className="space-y-2">
                  {req.examples.map((example, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {example}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Filing Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From use assessment to registration
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-[#FF6B35] to-[#FF8C42] text-white rounded-xl p-6 shadow-lg">
                  <div className="text-4xl font-bold mb-2 opacity-50">{item.step}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-sm mb-4 text-white/90">{item.description}</p>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Clock className="w-4 h-4" />
                    {item.timeline}
                  </div>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-[#FF6B35] to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Statement of Use Packages
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional filing services with expert review
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all relative ${
                  pkg.popular ? 'ring-4 ring-[#FF6B35] scale-105' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FF6B35] text-white px-6 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>
                  <div className="text-4xl font-bold text-[#FF6B35] mb-2">{pkg.price}</div>
                  <p className="text-sm text-gray-500">+ USPTO fees</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={() => openModal('registration')}
                  className={`w-full py-3 rounded-lg font-semibold transition-all ${
                    pkg.popular 
                      ? 'bg-[#FF6B35] hover:bg-[#FF8C42] text-white shadow-lg' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2">5,000+</div>
              <div className="text-gray-300">SOUs Filed</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">96%</div>
              <div className="text-gray-300">Approval Rate</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">3-5 Days</div>
              <div className="text-gray-300">Preparation Time</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">Zero</div>
              <div className="text-gray-300">Missed Deadlines</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about Statement of Use
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
                >
                  <span className="font-semibold text-gray-900 pr-8">{faq.question}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Don't Miss Your Deadline
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Complete your trademark registration with professional Statement of Use filing. Protect your priority date and secure full federal protection.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button 
              onClick={() => openModal('registration')}
              className="bg-white text-[#FF6B35] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:shadow-xl"
            >
              File Statement of Use
            </button>
            <button 
              onClick={() => {
                if (window.cd360Api && window.cd360Api.liveChat) {
                  window.cd360Api.liveChat.showWebWidget();
                } else {
                  console.log('Chat widget not loaded yet');
                }
              }}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#FF6B35] transition-all"
            >
              Speak with Attorney
            </button>
          </div>
        </div>
      </section>

      {modalMode && <RegistrationModal mode={modalMode} onClose={closeModal} />}
    </div>
  );
};

export default StatementOfUsePage;